const FLOW_NODE_WIDTH = 200;
const FLOW_NODE_HEIGHT = 96;
const DEADEND_NODE_SIZE = 44;

function stageHighlight(counts) {
    if ((counts?.pendingSkuCount || 0) > 0 || (counts?.fileCount || 0) > 0 || (counts?.skuCount || 0) > 0) {
        return 'pending';
    }
    return 'idle';
}

function isFiltered(filter) {
    return filter.hasFilter === true || filter.filterBy != null;
}

function buildFlowElements(processFlow, stageCounts) {
    const countByCpId = new Map((stageCounts || []).map(item => [item.cpId, item]));
    const nodes = [];
    const edges = [];
    const cpIdToNodeId = new Map();
    const deadendNodeIds = new Set();

    (processFlow || []).forEach((item, index) => {
        cpIdToNodeId.set(item.cpId, `node-${index}`);
    });

    const ensureDeadendNode = (sourceNodeId, highlight) => {
        const deadendNodeId = `deadend-${sourceNodeId}`;
        if (deadendNodeIds.has(deadendNodeId)) return deadendNodeId;
        deadendNodeIds.add(deadendNodeId);
        nodes.push({
            id: deadendNodeId,
            type: 'deadend',
            position: { x: 0, y: 0 },
            targetPosition: 'left',
            data: { isDeadend: true, sourceNodeId, highlight, active: highlight !== 'idle' },
            width: DEADEND_NODE_SIZE,
            height: DEADEND_NODE_SIZE,
        });
        return deadendNodeId;
    };

    (processFlow || []).forEach((item, index) => {
        const counts = countByCpId.get(item.cpId) || { fileCount: 0, skuCount: 0, pendingSkuCount: 0 };
        const fileCount = counts.fileCount || 0;
        const skuCount = counts.skuCount || 0;
        const highlight = stageHighlight(counts);
        nodes.push({
            id: `node-${index}`,
            type: 'stage',
            position: { x: 0, y: 0 },
            sourcePosition: 'right',
            targetPosition: 'left',
            data: {
                cpId: item.cpId,
                stageName: item.stageName || 'Unknown Stage',
                fileCount,
                skuCount,
                pendingCount: counts.pendingSkuCount || skuCount,
                highlight,
                active: highlight !== 'idle',
            },
            width: FLOW_NODE_WIDTH,
            height: FLOW_NODE_HEIGHT,
        });
    });

    (processFlow || []).forEach((item, index) => {
        const sourceNodeId = `node-${index}`;
        (item.filters || []).forEach((filter, filterIndex) => {
            const isDeadend = filter.next == null;
            const sourceHighlight = stageHighlight(countByCpId.get(item.cpId));
            const targetHighlight = isDeadend ? sourceHighlight : stageHighlight(countByCpId.get(filter.next));
            const sourceActive = sourceHighlight !== 'idle';
            const targetActive = targetHighlight !== 'idle';
            const targetNodeId = isDeadend
                ? ensureDeadendNode(sourceNodeId, sourceHighlight)
                : cpIdToNodeId.get(filter.next);
            if (!targetNodeId) return;
            const hasFilter = isFiltered(filter);
            const dimmed = !sourceActive && !targetActive;
            const stroke = hasFilter ? '#ff4d4f' : sourceHighlight === 'pending' || targetHighlight === 'pending' ? '#d48806' : '#52c41a';
            edges.push({
                id: `edge-${sourceNodeId}-${targetNodeId}-${filterIndex}`,
                source: sourceNodeId,
                target: targetNodeId,
                type: 'default',
                animated: !dimmed,
                style: {
                    stroke,
                    strokeWidth: dimmed ? 1 : 2.5,
                    opacity: dimmed ? 0.18 : 1,
                },
                data: { hasFilter, dimmed },
            });
        });
    });

    return { nodes, edges };
}

function applyLayoutPositions(baseGraph, layoutedChildren) {
    const nodePositions = new Map((layoutedChildren || []).map(node => [node.id, { x: node.x ?? 0, y: node.y ?? 0 }]));
    const deadendBySource = new Map();
    for (const node of baseGraph.nodes) {
        if (!node.data?.isDeadend || !node.data.sourceNodeId) continue;
        const list = deadendBySource.get(node.data.sourceNodeId) || [];
        list.push(node.id);
        deadendBySource.set(node.data.sourceNodeId, list);
    }

    const deadendPositions = new Map();
    deadendBySource.forEach((ids, sourceNodeId) => {
        const sourcePosition = nodePositions.get(sourceNodeId) || { x: 0, y: 0 };
        const spacing = DEADEND_NODE_SIZE + 16;
        const startY = -((ids.length - 1) * spacing) / 2;
        ids.forEach((id, index) => {
            deadendPositions.set(id, {
                x: sourcePosition.x + FLOW_NODE_WIDTH + 90,
                y: sourcePosition.y + startY + index * spacing,
            });
        });
    });

    return {
        nodes: baseGraph.nodes.map(node => ({
            ...node,
            position: node.data?.isDeadend
                ? deadendPositions.get(node.id) || { x: 0, y: 0 }
                : nodePositions.get(node.id) || { x: 0, y: 0 },
        })),
        edges: baseGraph.edges,
    };
}

function toElkGraph(baseGraph) {
    const regularNodes = baseGraph.nodes.filter(node => !node.data?.isDeadend);
    const layoutable = new Set(regularNodes.map(node => node.id));
    return {
        id: 'root',
        layoutOptions: {
            'elk.algorithm': 'layered',
            'elk.direction': 'RIGHT',
            'elk.layered.spacing.nodeNodeBetweenLayers': '150',
            'elk.spacing.nodeNode': '150',
            'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
            'elk.edgeRouting': 'POLYLINE',
        },
        children: regularNodes.map(node => ({
            id: node.id,
            width: node.width || FLOW_NODE_WIDTH,
            height: node.height || FLOW_NODE_HEIGHT,
        })),
        edges: baseGraph.edges
            .filter(edge => layoutable.has(edge.source) && layoutable.has(edge.target))
            .map(edge => ({ id: edge.id, sources: [edge.source], targets: [edge.target] })),
    };
}

const processFlowGraph = {
    FLOW_NODE_WIDTH,
    FLOW_NODE_HEIGHT,
    DEADEND_NODE_SIZE,
    stageHighlight,
    buildFlowElements,
    applyLayoutPositions,
    toElkGraph,
};

export default processFlowGraph;
export {
    FLOW_NODE_WIDTH,
    FLOW_NODE_HEIGHT,
    DEADEND_NODE_SIZE,
    stageHighlight,
    buildFlowElements,
    applyLayoutPositions,
    toElkGraph,
};
